import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, from, of } from 'rxjs';

export interface SpriteInfo {
  imageFileId: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

const STORAGE_KEY_PREFIX = 'pss_sprites_v';
const SPRITE_HOST = 'api.pixelstarships.com';
const SETTINGS_PATH = '/pss/' + SPRITE_HOST +
  '/SettingService/GetLatestVersion3?languageKey=en&deviceType=DeviceTypeAndroid';

@Injectable({ providedIn: 'root' })
export class SpriteCacheService {
  private map = new Map<string, SpriteInfo>();
  private readySubject = new BehaviorSubject<number>(0);
  private loadingPromise: Promise<void> | null = null;
  private currentVersion: string | null = null;

  /** Increments each time the sprite map is updated. Components can subscribe to re-render. */
  readonly ready$: Observable<number> = this.readySubject.asObservable();

  constructor(private http: HttpClient) {
    this.hydrateFromStorage();
  }

  get(spriteId: string | number | null | undefined): SpriteInfo | undefined {
    if (spriteId == null) return undefined;
    const key = String(spriteId);
    if (!key || key === '0') return undefined;
    return this.map.get(key);
  }

  /** Kick off a load + freshness check. Safe to call multiple times. */
  ensureLoaded(): Promise<void> {
    if (this.loadingPromise) return this.loadingPromise;
    this.loadingPromise = this.checkAndLoad();
    return this.loadingPromise;
  }

  private hydrateFromStorage(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
          const raw = localStorage.getItem(key);
          if (!raw) continue;
          const parsed = JSON.parse(raw) as Record<string, [number, number, number, number, number]>;
          this.map.clear();
          for (const id in parsed) {
            const t = parsed[id];
            this.map.set(id, { imageFileId: t[0], x: t[1], y: t[2], width: t[3], height: t[4] });
          }
          this.currentVersion = key.slice(STORAGE_KEY_PREFIX.length);
          this.readySubject.next(this.readySubject.value + 1);
          return;
        }
      }
    } catch {
      // Corrupt cache — ignore, will refetch.
    }
  }

  private async checkAndLoad(): Promise<void> {
    const liveVersion = await this.fetchSpriteVersion();
    if (!liveVersion) return; // Settings fetch failed; keep whatever cached map we have.
    if (this.currentVersion === liveVersion && this.map.size > 0) return;

    const xml = await this.fetchListSpritesXml(liveVersion);
    if (!xml) return;

    const parsed = this.parseListSprites(xml);
    if (parsed.size === 0) return;

    this.map = parsed;
    this.currentVersion = liveVersion;
    this.persistToStorage(liveVersion, parsed);
    this.readySubject.next(this.readySubject.value + 1);
  }

  private async fetchSpriteVersion(): Promise<string | null> {
    try {
      const xml = await this.http.get(SETTINGS_PATH, { responseType: 'text' }).toPromise();
      if (!xml) return null;
      const m = xml.match(/SpriteVersion="(\d+)"/);
      return m ? m[1] : null;
    } catch {
      return null;
    }
  }

  private async fetchListSpritesXml(version: string): Promise<string | null> {
    try {
      const url = '/pss/' + SPRITE_HOST + '/FileService/ListSprites?version=' + encodeURIComponent(version);
      const xml = await this.http.get(url, { responseType: 'text' }).toPromise();
      return xml || null;
    } catch {
      return null;
    }
  }

  private parseListSprites(xml: string): Map<string, SpriteInfo> {
    const out = new Map<string, SpriteInfo>();
    const re = /<Sprite\s+([^/>]+)\/>/g;
    let match: RegExpExecArray | null;
    while ((match = re.exec(xml)) !== null) {
      const attrs = match[1];
      const id = attrMatch(attrs, 'SpriteId');
      if (!id) continue;
      const imageFileId = parseInt(attrMatch(attrs, 'ImageFileId') || '0', 10);
      const x = parseInt(attrMatch(attrs, 'X') || '0', 10);
      const y = parseInt(attrMatch(attrs, 'Y') || '0', 10);
      const width = parseInt(attrMatch(attrs, 'Width') || '0', 10);
      const height = parseInt(attrMatch(attrs, 'Height') || '0', 10);
      if (!imageFileId || !width || !height) continue;
      out.set(id, { imageFileId, x, y, width, height });
    }
    return out;
  }

  private persistToStorage(version: string, map: Map<string, SpriteInfo>): void {
    if (typeof localStorage === 'undefined') return;
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && key.startsWith(STORAGE_KEY_PREFIX)) localStorage.removeItem(key);
      }
      const obj: Record<string, [number, number, number, number, number]> = {};
      map.forEach((v, k) => {
        obj[k] = [v.imageFileId, v.x, v.y, v.width, v.height];
      });
      localStorage.setItem(STORAGE_KEY_PREFIX + version, JSON.stringify(obj));
    } catch {
      // Quota or serialization issue — ignore. Next session will refetch.
    }
  }
}

function attrMatch(attrs: string, name: string): string | null {
  const re = new RegExp(name + '="([^"]*)"');
  const m = attrs.match(re);
  return m ? m[1] : null;
}
